import React, { useEffect, useRef, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { RefreshCw, Loader2, ShieldCheck, AlertTriangle, QrCode } from 'lucide-react';
import { useGenerateWalletQR, useWalletBalance } from '../../hooks/useWalletQueries';
import { toast } from 'sonner';

/**
 * Customer-facing QR dialog for in-venue wallet redemption.
 *
 * Behaviour:
 *  - On open: requests a fresh 60s JWT from /api/customer/wallet/qr-token
 *  - Renders the JWT as a QR code (the admin scanner decodes this)
 *  - Shows a live ticking countdown
 *  - Auto-refreshes the QR ~5 seconds before expiry to keep it always-valid
 *  - Refreshes balance on close so the deducted amount shows up immediately
 */
export default function RedeemQRDialog({ open, onClose }) {
  const generate = useGenerateWalletQR();
  const { refetch: refetchBalance } = useWalletBalance({ enabled: false });

  const [qr, setQr]     = useState(null);   // { token, jti, expires_at, ttl_seconds }
  const [secondsLeft, setSecondsLeft] = useState(0);
  const tickerRef = useRef(null);

  const fetchNewQR = useCallback(async () => {
    try {
      const res  = await generate.mutateAsync();
      const data = res.data;
      setQr(data);
      const exp = new Date(data.expires_at);
      setSecondsLeft(Math.max(0, Math.floor((exp - new Date()) / 1000)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate QR');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open → fetch first QR. Close → clear state + refresh balance.
  useEffect(() => {
    if (open) {
      fetchNewQR();
    } else {
      setQr(null);
      setSecondsLeft(0);
      // Pull latest balance — it may have been redeemed in the meantime
      refetchBalance();
    }
    return () => {
      if (tickerRef.current) clearInterval(tickerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Per-second ticker + auto-refresh ~5s before expiry
  useEffect(() => {
    if (!open || !qr) return undefined;
    tickerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        const next = s - 1;
        if (next <= 5 && next > 0 && !generate.isPending) {
          // Pre-fetch a new QR slightly before expiry — keeps display gap-free
          fetchNewQR();
        }
        return next > 0 ? next : 0;
      });
    }, 1000);
    return () => clearInterval(tickerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr, open]);

  const expired = secondsLeft <= 0 && !!qr;
  const ringColor = secondsLeft > 20 ? '#4ADE80' : secondsLeft > 8 ? '#FBB924' : '#FF4D4D';
  const dashOffset = qr ? (1 - secondsLeft / (qr.ttl_seconds || 60)) * 226 : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="wallet-qr-dialog"
        className="sm:max-w-md bg-[#0A0A0A] border border-[rgba(212,168,67,0.25)]"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-['EB_Garamond'] text-[#F2F2F2] flex items-center gap-2">
            <QrCode size={18} className="text-[#D4A843]" /> Redeem at Counter
          </DialogTitle>
          <DialogDescription className="text-xs text-[#B9B9B9]">
            Show this QR to the bar / counter staff. They will scan it and deduct the points you authorise.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center py-4 space-y-5">
          {/* QR + countdown ring */}
          <div className="relative">
            <div className="relative w-[280px] h-[280px] flex items-center justify-center rounded-2xl bg-white p-4">
              {generate.isPending && !qr ? (
                <Loader2 size={32} className="animate-spin text-[#0A0A0A]" />
              ) : qr && !expired ? (
                <QRCodeSVG
                  data-testid="wallet-qr-code"
                  value={qr.token}
                  size={248}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#0A0A0A"
                  marginSize={2}
                />
              ) : (
                <div className="text-center">
                  <AlertTriangle size={28} className="mx-auto mb-2 text-[#FF4D4D]" />
                  <p className="text-xs text-[#0A0A0A]">QR expired</p>
                </div>
              )}
            </div>

            {/* Countdown ring */}
            {qr && !expired && (
              <svg className="absolute -top-2 -left-2 w-[296px] h-[296px] -rotate-90 pointer-events-none">
                <circle
                  cx="148" cy="148" r="36"
                  className="fill-none"
                  stroke="rgba(255,255,255,0.05)"
                  strokeWidth="3"
                />
              </svg>
            )}
          </div>

          {/* Human-readable short code — admins can type this if scanning fails */}
          {qr && !expired && (
            <div
              data-testid="wallet-qr-short-code"
              className="text-center"
            >
              <p className="text-[10px] text-[#6B6B6B] uppercase tracking-widest mb-1">
                Or read this code to staff
              </p>
              <p className="text-2xl font-mono font-bold text-[#F2F2F2] tracking-[0.35em] select-all">
                {qr.token}
              </p>
            </div>
          )}

          {/* Countdown label */}
          <div className="text-center">
            {qr && !expired ? (
              <>
                <p
                  data-testid="wallet-qr-countdown"
                  className="text-3xl font-bold font-mono"
                  style={{ color: ringColor }}
                >
                  {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:{String(secondsLeft % 60).padStart(2, '0')}
                </p>
                <p className="text-xs text-[#6B6B6B] uppercase tracking-widest mt-1">QR refreshes automatically</p>
              </>
            ) : (
              <p className="text-sm text-[#FF8A8A]">Expired — generating a fresh code…</p>
            )}
          </div>

          {/* Security note */}
          <div className="flex items-start gap-2 text-[11px] text-[#6B6B6B] bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)] rounded-xl px-3 py-2">
            <ShieldCheck size={12} className="text-[#4ADE80] flex-shrink-0 mt-0.5" />
            <span>
              This QR is single-use and signed by Scratch. Never share a screenshot — it can only be redeemed once at our counter.
            </span>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            data-testid="wallet-qr-refresh"
            className="flex-1 border-[rgba(255,255,255,0.1)] text-[#B9B9B9]"
            onClick={fetchNewQR}
            disabled={generate.isPending}
          >
            <RefreshCw size={14} className={generate.isPending ? 'mr-2 animate-spin' : 'mr-2'} />
            New QR
          </Button>
          <Button
            data-testid="wallet-qr-close"
            className="flex-1 btn-gold"
            onClick={() => onClose?.()}
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
